import React, { useEffect, useMemo, useState } from "react";
import { getCockpitDriverMetrics } from "../../metricRegistry";

/**
 * Valuation (Negotiation Cockpit)
 * - Driver inputs only (from registry)
 * - Columns: Assessor / Actual / BA
 * - Saves numeric values to backend via onUpsertMetric
 * - DCF auto-calculates live from the current inputs.
 */

const SCENARIOS = [
  { key: "assessor", label: "Assessor" },
  { key: "actual", label: "Actual" },
  { key: "ba", label: "BA" },
];

function safeNum(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function toPercentDisplay(nDecimal) {
  if (nDecimal === null || nDecimal === undefined) return "";
  const n = Number(nDecimal);
  if (!Number.isFinite(n)) return "";
  return String((n * 100).toFixed(2)).replace(/\.00$/, "");
}

function percentInputToDecimal(nPercent) {
  if (nPercent === null || nPercent === undefined) return null;
  const n = Number(nPercent);
  if (!Number.isFinite(n)) return null;
  return n / 100.0;
}

function indexMetrics(metrics) {
  const map = new Map();
  for (const m of metrics || []) {
    const k = `${m.scenario}.${m.metric_code}`;
    map.set(k, m.value_num ?? null);
  }
  return map;
}

export default function ValuationTab({ well, resolved, metrics, onUpsertMetric, onPatchWell }) {
  const metricIndex = useMemo(() => indexMetrics(metrics), [metrics]);

  // ✅ Drivers for cockpit come from registry
  const VARIABLES = useMemo(() => {
    return (getCockpitDriverMetrics() || []).map((m) => ({
      code: m.code,
      label: m.label || m.code,
      unit: m.unit || null,
      kind: m.type, // number|money|percent|text (we treat "percent" specially below)
    }));
  }, []);

  const initialYears =
    Number.isFinite(Number(well?.forecast_years)) && Number(well?.forecast_years) > 0
      ? Number(well?.forecast_years)
      : 10;

  const [yearsDraft, setYearsDraft] = useState(String(initialYears));

  useEffect(() => {
    const next =
      Number.isFinite(Number(well?.forecast_years)) && Number(well?.forecast_years) > 0
        ? String(Number(well?.forecast_years))
        : "10";
    setYearsDraft(next);
  }, [well?.id, well?.forecast_years]);

  const years = useMemo(() => {
    const n = Number(String(yearsDraft).trim());
    if (!Number.isFinite(n)) return 10;
    const x = Math.round(n);
    return Math.max(1, Math.min(60, x));
  }, [yearsDraft]);

  async function saveForecastYears() {
    const n = Number(String(yearsDraft).trim());
    if (!Number.isFinite(n)) return;
    const x = Math.max(1, Math.min(60, Math.round(n)));
    const current = Number.isFinite(Number(well?.forecast_years)) ? Number(well?.forecast_years) : null;
    if (current === x) return;
    await onPatchWell?.({ forecast_years: x });
  }

  const [draft, setDraft] = useState({});

  useEffect(() => {
    const next = {};
    for (const s of SCENARIOS) {
      for (const v of VARIABLES) {
        const k = `${s.key}.${v.code}`;
        const stored = metricIndex.get(k);

        if (stored !== null && stored !== undefined) {
          if (v.kind === "percent") next[k] = toPercentDisplay(stored);
          else next[k] = String(stored);
          continue;
        }

        const r = (resolved || {})[v.code] || {};
        const fallback =
          s.key === "assessor" ? r.assessor :
          s.key === "actual" ? r.actual :
          null;

        if (fallback === null || fallback === undefined) {
          next[k] = "";
        } else {
          if (v.kind === "percent") next[k] = toPercentDisplay(fallback);
          else next[k] = String(fallback);
        }
      }
    }
    setDraft(next);
  }, [well?.id, metricIndex, resolved, VARIABLES]);

  async function saveCell(scenario, code, kind) {
    const k = `${scenario}.${code}`;
    const raw = draft[k];

    const n = safeNum(raw);
    if (n === null) return;

    let valueNum = n;
    if (kind === "percent") {
      valueNum = percentInputToDecimal(n);
      if (valueNum === null) return;
    }

    await onUpsertMetric?.(scenario, code, valueNum, null, unitFor(code));
  }

  function unitFor(code) {
    const v = VARIABLES.find((x) => x.code === code);
    return v?.unit || null;
  }

  // Minimal starter DCF (same as your current)
  const computedInputs = useMemo(() => {
    function getVal(scenario, code) {
      const vdef = VARIABLES.find((x) => x.code === code);
      const k = `${scenario}.${code}`;
      const n = safeNum(draft[k]);
      if (n === null) return null;

      if (vdef?.kind === "percent") return percentInputToDecimal(n);
      return n;
    }

    return {
      assessor: buildScenarioInputs("assessor", getVal),
      actual: buildScenarioInputs("actual", getVal),
      ba: buildScenarioInputs("ba", getVal),
    };
  }, [draft, VARIABLES]);

  const dcf = useMemo(() => {
    return {
      assessor: computeDCF(computedInputs.assessor, years),
      actual: computeDCF(computedInputs.actual, years),
      ba: computeDCF(computedInputs.ba, years),
    };
  }, [computedInputs, years]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-800">Valuation (Negotiation Cockpit)</div>
          <div className="text-xs text-gray-500">Driver inputs only. Values save on blur / Enter.</div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-500">Forecast years</div>
          <input
            className="input w-28"
            value={yearsDraft}
            onChange={(e) => setYearsDraft(e.target.value)}
            onBlur={saveForecastYears}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="py-2 px-3 text-left">Variable</th>
                {SCENARIOS.map((s) => (
                  <th key={s.key} className="py-2 px-3 text-left">{s.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {VARIABLES.map((v) => (
                <tr key={v.code} className="border-t">
                  <td className="py-2 px-3">
                    <div className="font-semibold text-gray-800">{v.label}</div>
                    <div className="text-xs text-gray-500">{v.unit || ""}</div>
                  </td>

                  {SCENARIOS.map((s) => {
                    const k = `${s.key}.${v.code}`;
                    return (
                      <td key={k} className="py-2 px-3">
                        <input
                          className="w-full rounded-lg border border-gray-200 px-2 py-1"
                          value={draft[k] ?? ""}
                          onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              await saveCell(s.key, v.code, v.kind);
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={async () => {
                            await saveCell(s.key, v.code, v.kind);
                          }}
                          placeholder={v.kind === "percent" ? "e.g. 18" : "e.g. 150000"}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-800 mb-2">DCF Summary</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Tile label="Assessor DCF Value" value={fmtMoney(dcf.assessor)} />
          <Tile label="Actual DCF Value" value={fmtMoney(dcf.actual)} />
          <Tile label="BA DCF Value" value={fmtMoney(dcf.ba)} />
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Current DCF is a simplified starter: Revenue − LOE. We’ll expand it as you add more drivers.
        </div>
      </div>

      <style>{`
        .input { border:1px solid #e5e7eb; background:#fff; padding:6px 10px; border-radius:10px; font-size:14px; }
        th, td { vertical-align: top; }
      `}</style>
    </div>
  );
}

function buildScenarioInputs(scenario, getVal) {
  return {
    production_boe: getVal(scenario, "production_boe"),
    oil_price: getVal(scenario, "oil_price"),
    loe: getVal(scenario, "loe"),
    decline_rate: getVal(scenario, "decline_rate") ?? 0,
    discount_rate: getVal(scenario, "discount_rate") ?? 0,
  };
}

function computeDCF(inp, years) {
  const production = inp.production_boe;
  const oilPrice = inp.oil_price;
  const loe = inp.loe;
  const declineRate = inp.decline_rate ?? 0;
  const discountRate = inp.discount_rate ?? 0;

  if (production === null || oilPrice === null || loe === null) return null;

  let dcfValue = 0;

  for (let t = 1; t <= years; t++) {
    const prodT = production * Math.pow(1 - declineRate, t - 1);
    const revT = prodT * oilPrice;
    const noiT = revT - loe;
    const pvT = noiT / Math.pow(1 + discountRate, t);
    if (Number.isFinite(pvT)) dcfValue += pvT;
  }

  return Number.isFinite(dcfValue) ? dcfValue : null;
}

function Tile({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function fmtMoney(x) {
  if (x === null || x === undefined || x === "") return "—";
  const n = typeof x === "number" ? x : Number(String(x).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(x);
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
